"use client";

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/utils/supabase/client';
import { showSuccess, showError } from '@/utils/toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Download } from 'lucide-react';

interface InvoicePDFGeneratorProps {
  invoiceId: string;
}

export const InvoicePDFGenerator = ({ invoiceId }: InvoicePDFGeneratorProps) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async () => {
    if (!invoiceId) return;

    setIsGenerating(true);
    try {
      // Fetch invoice data
      const { data: invoice, error: fetchError } = await supabase
        .from('invoices')
        .select('*, clients(name, email)')
        .eq('id', invoiceId)
        .single();

      if (fetchError) throw fetchError;
      if (!invoice) throw new Error('Invoice not found');

      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header with logo (if available) - skip for now (logo may not be present via relation)
      // ... keep header text ...

      // Company details
      doc.setFontSize(16);
      doc.text(invoice.company?.name || 'Your Company', 70, 20);
      doc.setFontSize(10);
      doc.text(`Invoice #${invoice.invoice_number}`, 70, 30);
      doc.text(`Date: ${new Date(invoice.created_at).toLocaleDateString()}`, 70, 38);
      if (invoice.due_date) {
        doc.text(`Due: ${new Date(invoice.due_date).toLocaleDateString()}`, 70, 46);
      }

      // Client details (right side)
      const clientY = 20;
      doc.text('Bill To:', pageWidth - 80, clientY);
      doc.text(invoice.clients?.name || 'Client', pageWidth - 80, clientY + 8);
      doc.text(invoice.clients?.email || '', pageWidth - 80, clientY + 16);

      // Table for line items (placeholder - fetch real items if table exists)
      const tableData = [
        ['Description', 'Qty', 'Rate', 'Total'],
        ['Consulting Services', 10, 50, 500],
        ['Additional Work', 5, 100, 500],
        ['', '', '', invoice.total_amount.toFixed(2)],
      ];

      (doc as any).autoTable({
        head: [tableData[0]],
        body: tableData.slice(1),
        startY: 70,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [41, 128, 185] },
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 20 }, 2: { cellWidth: 30 }, 3: { cellWidth: 30 } },
        margin: { left: 15, right: 15 },
      });

      // Footer with total
      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text('Total: $' + invoice.total_amount.toFixed(2), pageWidth - 60, finalY);

      // Save and upload
      const pdfBlob = doc.output('blob');
      const fileName = `invoice-${invoice.invoice_number}.pdf`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('invoices')
        .upload(`${invoice.company_id}/${fileName}`, pdfBlob, { contentType: 'application/pdf' });

      if (uploadError) throw uploadError;

      const publicUrl = supabase.storage.from('invoices').getPublicUrl(`${invoice.company_id}/${fileName}`).data.publicUrl;

      // Open uploaded PDF in a new tab for convenience
      if (publicUrl) {
        window.open(publicUrl, '_blank');
      }

      showSuccess(`Invoice PDF generated and saved!`);
    } catch (error) {
      console.error('PDF generation error:', error);
      showError('Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={generatePDF}
      disabled={isGenerating}
    >
      {isGenerating ? (
        <>
          <Download className="h-4 w-4 mr-2 animate-spin" />
          Generating...
        </>
      ) : (
        <>
          <Download className="h-4 w-4 mr-2" />
          Generate PDF
        </>
      )}
    </Button>
  );
};